import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLandingRoute } from '../config/navigation';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import './Login.css';

const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {off ? (
      <>
        <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.6 6.6A18.5 18.5 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 5.4-1.6" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <path d="m2 2 20 20" />
      </>
    ) : (
      <>
        <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const { companyName, logoUrl, loading: settingsLoading } = useSettings();
  const navigate = useNavigate();

  const brand = settingsLoading ? '' : companyName;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      // Land on the first page this account has permission to open
      const admin = result.admin;
      const can = (permission) =>
        Boolean(admin?.isSuperAdmin) || (admin?.permissions || []).includes(permission);
      navigate(getLandingRoute(can) || '/sy-admin');
    } else {
      setError(result.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="login">
      {/* ------------------------------ brand side ----------------------------- */}
      <aside className="login-brand">
        <span className="login-glow login-glow-a" aria-hidden="true" />
        <span className="login-glow login-glow-b" aria-hidden="true" />

        <div className="login-brand-inner">
          <div className="login-mark">
            {logoUrl ? (
              <img src={logoUrl} alt="" />
            ) : (
              <span aria-hidden="true">{(brand || 'W').charAt(0)}</span>
            )}
          </div>

          <h1>{brand || ' '}</h1>
          <p className="login-brand-line">Admin console</p>

          <ul className="login-points">
            <li>Manage hotels, spas, tours and travel packages</li>
            <li>Review owner registrations and customer reports</li>
            <li>Track subscriptions and what they earn</li>
          </ul>
        </div>

        <p className="login-brand-foot">
          &copy; {new Date().getFullYear()} {brand}
        </p>
      </aside>

      {/* ------------------------------- form side ----------------------------- */}
      <main className="login-panel">
        <div className="login-form-wrap">
          {/* Brand repeated here for narrow screens, where the panel is hidden */}
          <div className="login-mark login-mark-compact">
            {logoUrl ? (
              <img src={logoUrl} alt="" />
            ) : (
              <span aria-hidden="true">{(brand || 'W').charAt(0)}</span>
            )}
          </div>

          <h2>Sign in</h2>
          <p className="login-sub">Enter your credentials to open the admin panel.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="login-username">Username</label>
              <input
                id="login-username"
                name="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                placeholder="Your username"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <div className="login-password">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            {/* aria-live so a screen reader announces a failed attempt */}
            <div aria-live="polite">
              {error && <div className="error-message login-error">{error}</div>}
            </div>

            <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
              {loading && <span className="login-spinner" aria-hidden="true" />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="login-help">
            Lost access? Ask another administrator to reset your password.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
